import axios from "./config";
import { getToken } from "app/services/authService";
import {
  BrandsApiResponse,
  BundleApiResponse,
  CreditPackagesByPackageIdApiResponse,
  PackageApiResponse,
} from "app/types/common";
import { getActiveConfig } from "app/utils/constants/common";
import { AxiosResponse } from "axios";
import { pdfjs } from "react-pdf";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const { REACT_APP_BASE_URL, REACT_APP_GOOGLE_KEY } = process.env;

// TODO: Add api request and reponse types

export const getCountriesAPI = () =>
  axios.get("/Countries", {
    headers: {
      "accept-language": "",
    },
  });

export const getPackagesAPI = (query: string) => {
  return axios.get(`/Packages${query}`);
};

export const getPackagesv2API = (
  query: string,
): Promise<AxiosResponse<PackageApiResponse>> => {
  // set "Accept" header to "application/vnd.lightfields.comp.content.api.json" to get the new packages
  return axios.get<PackageApiResponse>(`/packages${query}`, {
    headers: {
      Accept: "application/vnd.lightfields.comp.content.api.json",
    },
  });
};

export const getCreditPackagesByPackageIdApi = (
  packageId: number,
): Promise<AxiosResponse<CreditPackagesByPackageIdApiResponse>> =>
  axios.get<CreditPackagesByPackageIdApiResponse>(
    `/packages/${packageId}/credit-packages`,
  );

export const getBrandsAPI = (): Promise<AxiosResponse<BrandsApiResponse>> =>
  axios.get<BrandsApiResponse>("/brands");

export const getStoresAPI = () => axios.get("/stores");

export const getBundlesAPI = (query: string) => axios.get(`/bundles${query}`);

export const getBundlesByPackageIdApi = (
  packageId: number,
): Promise<AxiosResponse<BundleApiResponse>> =>
  axios.get<BundleApiResponse>(`/packages/${packageId}/bundles`, {
    headers: {
      Accept: "application/vnd.lightfields.comp.content.api.json",
    },
  });

export const getGovenratesAndDistrictsApi = () => {
  // NOTE: for now this is only for kw/ once other locale is enable we need to adjust this.
  const { activeLocale } = getActiveConfig();
  return axios.get(
    `/provinces/${activeLocale.defaultProvinceId}/all-districts`,
  );
};

export const getPatmentProvidersAPI = (storeId: any) => {
  return axios.get(`/Stores/${storeId}/payment-providers`, {
    headers: {
      Application: "dashboard",
    },
  });
};

export const getAllPreferencesAPI = () => axios.get("/items");

export const getConfigurationsAPI = (payload: any) => {
  const { locale, application } = payload;
  return axios.get(`/configurations?locale=${locale}`, {
    headers: {
      Application: application,
    },
  });
};

export const getAllergiesAPI = () => axios.get(`/allergic-Items`);

// get governorate, area, block from google api
export const getGoogleMapDataAPI = (lat: any, lng: any) => {
  return fetch(
    `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&result_type=neighborhood&key=${REACT_APP_GOOGLE_KEY}&language=en`,
  );
};
export const downloadLinkAPI = async (
  uri: string,
  fileName: string,
): Promise<void> => {
  try {
    const url = `${REACT_APP_BASE_URL}${uri}`;
    const token = getToken();

    const response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 404) {
      const errorMessage = await response.text();
      throw new Error(errorMessage || "File not found.");
    }

    if (response.status !== 200) {
      throw new Error(
        `Download failed: ${response.status} ${response.statusText}`,
      );
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error: unknown) {
    return;
  }
};

export const downloadAndPreview = (uri: string) => {
  const url = `${REACT_APP_BASE_URL}${uri}`;
  const headers = new Headers();
  const token = getToken();
  headers.append("Authorization", `Bearer ${token}`);

  fetch(url, {
    method: "GET",
    headers,
  })
    .then((response) => {
      const contentType = response.headers.get("content-type");
      return response.blob().then((blob) => ({ blob, contentType }));
    })
    .then(({ blob, contentType }) => {
      // Check if the response is a PDF
      if (contentType === "application/pdf") {
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, "_blank"); // Open in a new tab
      }
    })
    .catch((error) => console.error(error));
};

const getFileNameFromUrl = (downloadURL: string): string => {
  const urlPath = downloadURL.split("?")[0];
  return (
    decodeURIComponent(urlPath.substring(urlPath.lastIndexOf("/") + 1)) ||
    "download"
  );
};

const downloadSignedUrl = async (downloadURL: string): Promise<void> => {
  try {
    const response = await fetch(downloadURL);
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = getFileNameFromUrl(downloadURL);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch {
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = downloadURL;
    document.body.appendChild(iframe);
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 60000);
  }
};

export const downloadLinkAPIV2 = (uri: string, showAlert: boolean = true) => {
  const url = `${REACT_APP_BASE_URL}${uri}`;
  const headers = new Headers();
  const token = getToken();
  headers.append("Authorization", `Bearer ${token}`);

  fetch(url, {
    method: "GET",
    headers,
  })
    .then(async (response) => {
      if (response.status !== 200) {
        const errorBody = await response.json().catch((): null => null);
        const message =
          errorBody?.error?.message ||
          errorBody?.message ||
          errorBody?.Errors?.[0]?.Message ||
          `Download failed: ${response.status} ${response.statusText}`;
        throw new Error(message);
      }
      return response.json();
    })
    .then((data) => {
      const downloadURL = data.url;
      const downloadWindow = window.open(downloadURL);
      setTimeout(() => {
        downloadWindow?.close();
      }, 5000); // Close the window after 5 seconds, adjust if needed
    })
    .catch((error: unknown) => {
      const message =
        error instanceof Error ? error.message : "Something went wrong";
      if (showAlert) {
        window.alert(message);
      }
    });
};

export const downloadMultipleLinkAPIV2 = (
  uri: string,
  showAlert: boolean = true,
) => {
  const url = `${REACT_APP_BASE_URL}${uri}`;
  const headers = new Headers();
  const token = getToken();
  headers.append("Authorization", `Bearer ${token}`);

  fetch(url, {
    method: "GET",
    headers,
  })
    .then(async (response) => {
      if (response.status !== 200) {
        const errorBody = await response.json().catch((): null => null);
        const message =
          errorBody?.error?.message ||
          errorBody?.message ||
          errorBody?.Errors?.[0]?.Message ||
          `Download failed: ${response.status} ${response.statusText}`;
        throw new Error(message);
      }
      return response.json();
    })
    .then(async (data: { url?: string; url2?: string }) => {
      const downloadUrls = [data.url, data.url2].filter(
        (downloadURL): downloadURL is string => Boolean(downloadURL),
      );

      for (const downloadURL of downloadUrls) {
        await downloadSignedUrl(downloadURL);
      }
    })
    .catch((error: unknown) => {
      const message =
        error instanceof Error ? error.message : "Something went wrong";
      if (showAlert) {
        window.alert(message);
      }
    });
};

export const printDownloadLinkAPI = (uri: any, printer: any) => {
  const url = `${REACT_APP_BASE_URL}${uri}`;
  const headers = new Headers();
  const token = getToken();
  headers.append("Authorization", `Bearer ${token}`);

  fetch(url, {
    method: "GET",
    headers,
  })
    .then((response) => response.arrayBuffer())
    .then((arrayBuffer) => {
      // Convert the array buffer to a PDF document
      return pdfjs.getDocument(arrayBuffer).promise;
    })
    .then((pdf) => {
      // Render the first page of the PDF on a canvas
      return pdf.getPage(1).then((page) => {
        const scale = 2.5;
        const viewport = page.getViewport({ scale });

        const offScreenCanvas = document.createElement("canvas");
        const offScreenContext = offScreenCanvas.getContext("2d");
        offScreenCanvas.width = viewport.width;
        offScreenCanvas.height = viewport.height;

        const renderContext = {
          canvasContext: offScreenContext,
          viewport: viewport,
        };

        return page.render(renderContext).promise.then(() => {
          // Convert the off-screen canvas to data URL
          return offScreenCanvas.toDataURL("image/png");
        });
      });
    })
    .then((dataUrl) => {
      // Create an image element to hold the PNG data
      const img = new Image();
      img.src = dataUrl;
      img.onload = function () {
        // Call the function to print the PNG image
        if (printer) {
          printer.brightness = 1.0;
          printer.halftone = printer.HALFTONE_THRESHOLD;
          const offScreenCanvas: HTMLCanvasElement =
            document.createElement("canvas");
          offScreenCanvas.id = "canvas";
          document.body.appendChild(offScreenCanvas);
          const ctx: CanvasRenderingContext2D | null =
            offScreenCanvas.getContext("2d");
          if (ctx) {
            offScreenCanvas.width = img.width;
            offScreenCanvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            printer.addImage(
              ctx,
              0,
              0,
              offScreenCanvas.width,
              offScreenCanvas.height,
              printer.COLOR_1,
              printer.MODE_MONO,
            );
            printer.addCut(printer.CUT_FEED);
            printer.send();

            // Remove the canvas element once printing is complete
            document.body.removeChild(offScreenCanvas);
          }
        }
      };
    });
};
